import React, { lazy, Suspense, useEffect, useState } from 'react';
import { BrowserRouter, Link, Navigate, Outlet, Route, Routes, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import './index.css';
import api, { getApiError } from './api/client';
import Layout from './components/Layout';
import HomePage from './pages/HomePage';
import Login from './pages/Login';
import SetupPage from './pages/SetupPage';
import LocationSelectPage from './pages/LocationSelectPage';
import { useAuthStore } from './store/authStore';
import { useTenantStore } from './store/tenantStore';

const Dashboard = lazy(() => import('./pages/Dashboard'));
const ModuleLanding = lazy(() => import('./pages/ModuleLanding'));
const LeadsPage = lazy(() => import('./pages/leads/LeadsPage'));
const CustomersPage = lazy(() => import('./pages/crm/CustomersPage'));
const ContactsPage = lazy(() => import('./pages/crm/ContactsPage'));
const OpportunitiesPage = lazy(() => import('./pages/crm/OpportunitiesPage'));
const CampaignsPage = lazy(() => import('./pages/leads/CampaignsPage'));
const SalesOrdersPage = lazy(() => import('./pages/sales/SalesOrdersPage'));
const InvoicesPage = lazy(() => import('./pages/sales/InvoicesPage'));
const PaymentsPage = lazy(() => import('./pages/sales/PaymentsPage'));
const VendorsPage = lazy(() => import('./pages/procurement/VendorsPage'));
const PurchaseOrdersPage = lazy(() => import('./pages/procurement/PurchaseOrdersPage'));
const ProductsPage = lazy(() => import('./pages/inventory/ProductsPage'));
const StockPage = lazy(() => import('./pages/inventory/StockPage'));
const BOMsPage = lazy(() => import('./pages/manufacturing/BOMsPage'));
const WorkOrdersPage = lazy(() => import('./pages/manufacturing/WorkOrdersPage'));
const QualityPage = lazy(() => import('./pages/quality/QualityPage'));
const AccountsPage = lazy(() => import('./pages/finance/AccountsPage'));
const JournalsPage = lazy(() => import('./pages/finance/JournalsPage'));
const ExpensesPage = lazy(() => import('./pages/finance/ExpensesPage'));
const AgingReportsPage = lazy(() => import('./pages/finance/AgingReportsPage'));
const EmployeesPage = lazy(() => import('./pages/hr/EmployeesPage'));
const TravelPage = lazy(() => import('./pages/hr/TravelPage'));
const AttendancePage = lazy(() => import('./pages/hr/AttendancePage'));
const LeavesPage = lazy(() => import('./pages/hr/LeavesPage'));
const PayrollPage = lazy(() => import('./pages/hr/PayrollPage'));
const ProjectsPage = lazy(() => import('./pages/projects/ProjectsPage'));
const TasksPage = lazy(() => import('./pages/projects/TasksPage'));
const ProjectDetailPage = lazy(() => import('./pages/projects/ProjectDetailPage'));
const TimesheetsPage = lazy(() => import('./pages/projects/TimesheetsPage'));
const TicketsPage = lazy(() => import('./pages/helpdesk/TicketsPage'));
const KnowledgeBasePage = lazy(() => import('./pages/helpdesk/KnowledgeBasePage'));
const AssetsPage = lazy(() => import('./pages/assets/AssetsPage'));
const DocumentsPage = lazy(() => import('./pages/documents/DocumentsPage'));
const ReportsPage = lazy(() => import('./pages/ReportsPage'));
const UsersPage = lazy(() => import('./pages/admin/UsersPage'));
const RolesPage = lazy(() => import('./pages/admin/RolesPage'));
const ApprovalsPage = lazy(() => import('./pages/approvals/ApprovalsPage'));
const CompanySettingsPage = lazy(() => import('./pages/admin/CompanySettingsPage'));
const ClientsPage = lazy(() => import('./pages/admin/ClientsPage'));
const SuperAdminPage = lazy(() => import('./pages/admin/SuperAdminPage'));
const PrintInvoicePage = lazy(() => import('./pages/sales/PrintInvoicePage'));
const GlobalPayrollPage = lazy(() => import('./pages/hr/GlobalPayrollPage'));
const EmployeeDetailPage = lazy(() => import('./pages/hr/EmployeeDetailPage'));
const MySpacePage = lazy(() => import('./pages/myspace/MySpacePage'));
const ResourcePlanningPage = lazy(() => import('./pages/hr/ResourcePlanningPage'));
const LeaveModulePage = lazy(() => import('./pages/hr/LeaveModulePage'));
const TaxPage = lazy(() => import('./pages/tax/TaxPage'));
const InventoryPage = lazy(() => import('./pages/inventory/InventoryPage'));
const WarehousesPage = lazy(() => import('./pages/inventory/WarehousesPage'));
const ResourcePage = lazy(() => import('./pages/resource/ResourcePage'));
const DealerModulePage = lazy(() => import('./pages/dealer/DealerModulePage'));
const SupplyChainDashboard = lazy(() => import('./pages/supplychain/SupplyChainDashboard'));
const GoodsReceiptPage = lazy(() => import('./pages/supplychain/GoodsReceiptPage'));
const LogisticsPage = lazy(() => import('./pages/supplychain/LogisticsPage'));
const SLAPage = lazy(() => import('./pages/helpdesk/SLAPage'));
const MastersPage = lazy(() => import('./pages/admin/MastersPage'));
const MRPPage = lazy(() => import('./pages/manufacturing/MRPPage'));
const QuotesPage = lazy(() => import('./pages/sales/QuotesPage'));
const BankReconPage = lazy(() => import('./pages/finance/BankReconPage'));
const RFQPage = lazy(() => import('./pages/procurement/RFQPage'));
const GanttPage = lazy(() => import('./pages/projects/GanttPage'));
const SkillsAdminPage = lazy(() => import('./pages/hr/SkillsAdminPage'));
const AuditLogPage = lazy(() => import('./pages/admin/AuditLogPage'));
const BatchesPage = lazy(() => import('./pages/inventory/BatchesPage'));

const CompanyShell: React.FC = () => {
  const { companySlug } = useParams();
  const setCompany = useTenantStore(s => s.setCompany);
  useEffect(() => {
    if (companySlug) {
      setCompany(companySlug);
      localStorage.setItem('companySlug', companySlug);
    }
  }, [companySlug, setCompany]);
  return <Outlet />;
};

interface SimplePageProps { title: string; icon: string; apiPath: string; }
const SimplePage: React.FC<SimplePageProps> = ({ title, icon, apiPath }) => {
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get(apiPath, { params: { limit: 50 } })
      .then((r: any) => setItems(Array.isArray(r.data) ? r.data : (r.data.items || [])))
      .catch(e => toast.error(getApiError(e, 'Failed to load data')))
      .finally(() => setLoading(false));
  }, [apiPath]);
  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center gap-3"><span className="text-4xl">{icon}</span>
        <div><h1 className="page-title">{title}</h1><p className="text-content-secondary text-sm">{items.length} records</p></div></div>
      <div className="card p-8">
        {loading ? <div className="text-center text-content-muted py-8">Loading…</div>
          : items.length === 0 ? <div className="text-center py-12 text-content-muted">No records yet</div>
          : <div className="overflow-x-auto"><table className="w-full text-sm"><thead><tr>{Object.keys(items[0]).slice(0,8).map(k=><th key={k} className="text-left px-4 py-2">{k.replaceAll('_',' ')}</th>)}</tr></thead><tbody>{items.map((item:any,i:number)=><tr key={item.id||i} className="border-t">{Object.entries(item).slice(0,8).map(([k,v]:any)=><td key={k} className="px-4 py-3">{String(v??'')}</td>)}</tr>)}</tbody></table></div>}
      </div>
    </div>
  );
};

const LoadingScreen = () => <div className="min-h-[50vh] grid place-items-center text-content-secondary"><div className="flex items-center gap-3"><span className="w-5 h-5 border-2 border-primary-200 border-t-primary-600 rounded-full animate-spin"/>Loading module…</div></div>;

const ProtectedRoute: React.FC<{ children: React.ReactNode; ready: boolean }> = ({ children, ready }) => {
  const { isAuthenticated } = useAuthStore();
  const { companySlug } = useParams();
  const slug = companySlug || localStorage.getItem('companySlug') || 'default';
  if (!ready) return <LoadingScreen />;
  return isAuthenticated ? <>{children}</> : <Navigate to={`/${slug}/login`} replace />;
};

function AppRoutes() {
  const { token, login, logout } = useAuthStore();
  const [authReady, setAuthReady] = useState(!token);
  useEffect(() => {
    if (!token) { setAuthReady(true); return; }
    let active = true;
    api.get('/auth/me')
      .then(r => { if (active) login(token, r.data); })
      .catch(() => { if (active) logout(); })
      .finally(() => { if (active) setAuthReady(true); });
    return () => { active = false; };
  }, []);

  const guard = (node: React.ReactNode) => <ProtectedRoute ready={authReady}>{node}</ProtectedRoute>;

  return (
    <Suspense fallback={<LoadingScreen />}>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/login" element={<Navigate to="/default/login" replace />} />
        <Route path="/modules" element={<Navigate to="/default/modules" replace />} />
        <Route path="/setup" element={<Navigate to="/default/setup" replace />} />
        <Route path="/dashboard" element={<Navigate to="/default/dashboard" replace />} />

        <Route path="/:companySlug" element={<CompanyShell />}>
          <Route path="login" element={<Login />} />
          <Route path="setup" element={<SetupPage />} />
          <Route path="location" element={guard(<LocationSelectPage />)} />
          <Route path="modules" element={guard(<ModuleLanding />)} />
          <Route path="print/:type/:id" element={guard(<PrintInvoicePage />)} />
          <Route element={guard(<Layout />)}>
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="leads" element={<LeadsPage />} />
            <Route path="leads/campaigns" element={<CampaignsPage />} />
            <Route path="crm/customers" element={<CustomersPage />} />
            <Route path="crm/contacts" element={<ContactsPage />} />
            <Route path="crm/opportunities" element={<OpportunitiesPage />} />
            <Route path="sales/orders" element={<SalesOrdersPage />} />
            <Route path="sales/quotes" element={<QuotesPage />} />
            <Route path="sales/invoices" element={<InvoicesPage />} />
            <Route path="sales/payments" element={<PaymentsPage />} />
            <Route path="supply-chain" element={<SupplyChainDashboard />} />
            <Route path="supply-chain/grn" element={<GoodsReceiptPage />} />
            <Route path="supply-chain/logistics" element={<LogisticsPage />} />
            <Route path="procurement/vendors" element={<VendorsPage />} />
            <Route path="procurement/orders" element={<PurchaseOrdersPage />} />
            <Route path="procurement/rfq" element={<RFQPage />} />
            <Route path="inventory/products" element={<ProductsPage />} />
            <Route path="inventory/stock" element={<StockPage />} />
            <Route path="inventory/batches" element={<BatchesPage />} />
            <Route path="inventory/warehouses" element={<WarehousesPage />} />
            <Route path="inventory/manage" element={<InventoryPage />} />
            <Route path="resource" element={<ResourcePage />} />
            <Route path="dealer" element={<DealerModulePage />} />
            <Route path="manufacturing/boms" element={<BOMsPage />} />
            <Route path="manufacturing/workorders" element={<WorkOrdersPage />} />
            <Route path="manufacturing/mrp" element={<MRPPage />} />
            <Route path="quality/checks" element={<QualityPage />} />
            <Route path="finance/accounts" element={<AccountsPage />} />
            <Route path="finance/journals" element={<JournalsPage />} />
            <Route path="finance/expenses" element={<ExpensesPage />} />
            <Route path="finance/aging" element={<AgingReportsPage />} />
            <Route path="finance/bank-recon" element={<BankReconPage />} />
            <Route path="hr/employees" element={<EmployeesPage />} />
            <Route path="hr/travel" element={<TravelPage />} />
            <Route path="hr/attendance" element={<AttendancePage />} />
            <Route path="hr/leaves" element={<LeavesPage />} />
            <Route path="hr/payroll" element={<PayrollPage />} />
            <Route path="hr/payroll-global" element={<GlobalPayrollPage />} />
            <Route path="hr/skills" element={<SkillsAdminPage />} />
            <Route path="hr/employees/:id" element={<EmployeeDetailPage />} />
            <Route path="hr/resource-planning" element={<ResourcePlanningPage />} />
            <Route path="my-space" element={<MySpacePage />} />
            <Route path="hr/leave-module" element={<LeaveModulePage />} />
            <Route path="projects" element={<ProjectsPage />} />
            <Route path="projects/:id" element={<ProjectDetailPage />} />
            <Route path="projects/tasks" element={<TasksPage />} />
            <Route path="projects/gantt" element={<GanttPage />} />
            <Route path="projects/timesheets" element={<TimesheetsPage />} />
            <Route path="helpdesk/tickets" element={<TicketsPage />} />
            <Route path="helpdesk/knowledge" element={<KnowledgeBasePage />} />
            <Route path="helpdesk/sla" element={<SLAPage />} />
            <Route path="assets" element={<AssetsPage />} />
            <Route path="documents" element={<DocumentsPage />} />
            <Route path="reports" element={<ReportsPage />} />
            <Route path="admin/users" element={<UsersPage />} />
            <Route path="admin/roles" element={<RolesPage />} />
            <Route path="admin/company" element={<CompanySettingsPage />} />
            <Route path="admin/clients" element={<ClientsPage />} />
            <Route path="admin/platform" element={<SuperAdminPage />} />
            <Route path="admin/audit" element={<AuditLogPage />} />
            <Route path="admin/masters" element={<MastersPage />} />
            <Route path="approvals" element={<ApprovalsPage />} />
            <Route path="tax" element={<TaxPage />} />
            <Route path="admin/settings" element={<div className="p-8 text-center text-content-muted text-lg">Settings — Coming Soon</div>} />
          </Route>
        </Route>

        <Route path="*" element={
          <div className="min-h-screen grid place-items-center bg-surface-muted">
            <div className="text-center">
              <div className="text-7xl font-bold text-primary-100">404</div>
              <h1 className="page-title">Page not found</h1>
              <Link to="/" className="inline-block mt-5 text-primary-600">Return home</Link>
            </div>
          </div>
        } />
      </Routes>
    </Suspense>
  );
}

export default function App() {
  return <BrowserRouter><AppRoutes /></BrowserRouter>;
}
